import React from 'react';
import Icon from '@/shared/components/Icon';
import type {BiomarkerStatus} from '@/shared/types/biomarker';
import {severityTier, type SeverityTier} from '@/shared/biomarkers/classify';

interface TrendCardProps {
    trend: {
        name: string;
        value: string;
        change: string;
        changeType: 'up' | 'down';
        status?: BiomarkerStatus;
    };
    onClick?: () => void;
    /**
     * Uniform card width in px, measured at runtime by useTrendCardLayout.
     * Omitted in the mobile stack, where the card fills its parent.
     */
    width?: number;
}

// Chip background, border, and accent color per severity tier, matching
// the v5 design's pastel rounded-rectangle chips with status-tinted borders
// (mint=normal, peach=borderline, rose=critical). Keyed by the shared
// SeverityTier so a status's chip color here always agrees with its color on
// the Heart Health card, counts, and chart zones (see severityTier in classify.ts).
const TIER_STYLES: Record<SeverityTier, {bg: string; border: string; accent: string}> = {
    normal: {bg: 'bg-mld-primary/10', border: 'border-mld-primary', accent: 'text-mld-status-normal'},
    borderline: {bg: 'bg-mld-status-borderline/20', border: 'border-mld-status-borderline', accent: 'text-amber-700'},
    critical: {bg: 'bg-mld-status-high/15', border: 'border-mld-status-high', accent: 'text-mld-status-high'},
    unknown: {bg: 'bg-gray-100', border: 'border-gray-200', accent: 'text-mld-text-primary'},
};

function bucketStatus(status?: BiomarkerStatus): SeverityTier {
    return status ? severityTier(status) : 'unknown';
}

const TrendCard: React.FC<TrendCardProps> = ({trend, onClick, width}) => {
    const {name, value, changeType, status} = trend;
    const isUp = changeType === 'up';
    const bucket = bucketStatus(status);
    const {bg, border, accent} = TIER_STYLES[bucket];

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
        }
    };

    return (
        <div
            // px-4 (padding), gap-3 (value/name gap), and border are mirrored by
            // CARD_CHROME_PARTS in utils/trendCardLayout.ts — change one here and
            // you must change the other, or the measured width and the rendered
            // card silently desync.
            className={`relative ${bg} border ${border} rounded-xl px-4 py-3 flex items-center gap-3 w-full flex-shrink-0 cursor-pointer hover:shadow-sm transition-shadow`}
            style={width === undefined ? undefined : {width}}
            onClick={onClick}
            role="button"
            tabIndex={0}
            onKeyDown={handleKeyDown}
            title={`${name} ${value}`}>
            {/* Trend arrow + large value */}
            <div className={`flex items-center gap-0.5 flex-shrink-0 ${accent}`}>
                {isUp ? (
                    <Icon name="arrow-trend-up-solid" className="h-4 w-4" />
                ) : (
                    <Icon name="arrow-trend-down-solid" className="h-4 w-4" />
                )}
                <span className="text-2xl font-normal leading-none">{value}</span>
            </div>
            {/* Biomarker name. Below md the horizontal strip is display:none, so
                its container is never measured and width stays undefined — there
                the name must wrap freely (whitespace-normal) so it is never
                clipped, and the card simply grows taller. At md+ the measured
                card width guarantees the name fits on one line
                (md:whitespace-nowrap). pr-4 keeps it clear of the magnifier in
                the top-right corner. */}
            <span className="text-xs font-medium text-mld-text-primary flex-1 min-w-0 leading-tight whitespace-normal md:whitespace-nowrap pr-4">
                {name}
            </span>
            {/* Magnifier — top-right corner */}
            <Icon name="magnifying-glass" className="absolute right-2.5 top-2.5 h-3.5 w-3.5 text-mld-muted-text" />
        </div>
    );
};

export default TrendCard;
